import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, TrendingUp, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeasonTotals() {
  const navigate = useNavigate();
  const { isAuthenticated, isPro, isLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('totals'); // 'totals' or 'averages'
  const [eventFilter, setEventFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  // Keep a stable copy of the filter option lists so the dropdowns don't
  // collapse when the currently active filter narrows the response payload.
  const [availableEvents, setAvailableEvents] = useState([]);
  const [availableSeasons, setAvailableSeasons] = useState([]);

  const fetchTotals = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (eventFilter && eventFilter !== 'all') params.set('event_id', eventFilter);
      if (seasonFilter && seasonFilter !== 'all') params.set('season_id', seasonFilter);
      const qs = params.toString();
      const res = await fetch(`${API_URL}/api/stats/season-totals${qs ? `?${qs}` : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        // Only refresh the option lists from an *unfiltered* response so the
        // dropdowns retain every league/season even when a filter is active.
        if (eventFilter === 'all' && seasonFilter === 'all') {
          setAvailableEvents(d.available_events || []);
          setAvailableSeasons(d.available_seasons || []);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [eventFilter, seasonFilter]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || !isPro) { navigate('/stats'); return; }
    fetchTotals();
  }, [isAuthenticated, isPro, isLoading, fetchTotals, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/stats')} className="px-2 sm:px-3 shrink-0" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>SEASON STATS</h1>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 shrink-0">
            <button className={`px-3 py-1 text-xs font-bold rounded ${view === 'totals' ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
              onClick={() => setView('totals')} data-testid="view-totals">TOTALS</button>
            <button className={`px-3 py-1 text-xs font-bold rounded ${view === 'averages' ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
              onClick={() => setView('averages')} data-testid="view-averages">AVERAGES</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* League + Season scope filters. Persist independently so the user
            can compare, e.g., a single league across all seasons or a single
            season across all leagues. */}
        <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="season-filter-row">
          <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Filter:</span>
          </div>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-full sm:w-[240px] h-9" data-testid="tournament-filter-select">
              <SelectValue placeholder="All Tournaments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="tournament-filter-all">All Tournaments</SelectItem>
              {availableEvents.map(ev => (
                <SelectItem key={ev.id} value={ev.id} data-testid={`tournament-filter-${ev.id}`}>
                  {ev.name} {ev.games_count ? `(${ev.games_count})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={seasonFilter} onValueChange={setSeasonFilter}>
            <SelectTrigger className="w-full sm:w-[200px] h-9" data-testid="season-filter-select">
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="season-filter-all">All Seasons</SelectItem>
              {availableSeasons.map(s => (
                <SelectItem key={s.id} value={s.id} data-testid={`season-filter-${s.id}`}>
                  {s.name} {s.games_count ? `(${s.games_count})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {availableEvents.length === 0 && availableSeasons.length === 0 && (
            <span className="text-xs text-slate-400">
              Link games to a tournament or season at setup to enable filtering.
            </span>
          )}
        </div>

        {!data || data.players.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {eventFilter !== 'all' || seasonFilter !== 'all'
                ? 'No completed games match this filter.'
                : 'No completed games yet. Season stats will appear here after you finish games.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">{data.games_count} game{data.games_count !== 1 ? 's' : ''} completed</p>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="season-table">
                    <thead>
                      <tr className="bg-[#00247D] text-white text-[10px] font-bold uppercase">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">Player</th>
                        <th className="px-3 py-2">Team</th>
                        <th className="px-3 py-2 text-center">GP</th>
                        <th className="px-3 py-2 text-center">PTS</th>
                        <th className="px-3 py-2 text-center">REB</th>
                        <th className="px-3 py-2 text-center">AST</th>
                        <th className="px-3 py-2 text-center">STL</th>
                        <th className="px-3 py-2 text-center">BLK</th>
                        <th className="px-3 py-2 text-center">TO</th>
                        <th className="px-3 py-2 text-center">FGM</th>
                        <th className="px-3 py-2 text-center">FGA</th>
                        <th className="px-3 py-2 text-center">FG%</th>
                        <th className="px-3 py-2 text-center">3PM</th>
                        <th className="px-3 py-2 text-center">3PA</th>
                        <th className="px-3 py-2 text-center">FTM</th>
                        <th className="px-3 py-2 text-center">FTA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.players.map((p, i) => {
                        const stats = view === 'totals' ? p.totals : p.averages;
                        return (
                          <tr key={p.player_id} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50'}`}>
                            <td className="px-3 py-2 text-sm font-mono text-slate-400">{p.jersey}</td>
                            <td className="px-3 py-2 text-sm font-semibold text-slate-800">{p.name}</td>
                            <td className="px-3 py-2 text-sm text-slate-500">{p.team_name}</td>
                            <td className="px-3 py-2 text-sm text-center">{p.games_played}</td>
                            <td className="px-3 py-2 text-sm text-center font-bold text-[#00247D]">{stats.pts}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.reb}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.ast}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.stl}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.blk}</td>
                            <td className="px-3 py-2 text-sm text-center text-red-500">{stats.to}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.fgm}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.fga}</td>
                            <td className="px-3 py-2 text-sm text-center">{view === 'totals' ? `${p.totals.fg_pct}%` : '-'}</td>
                            <td className="px-3 py-2 text-sm text-center">{view === 'totals' ? stats.three_m : stats.fgm}</td>
                            <td className="px-3 py-2 text-sm text-center">{view === 'totals' ? stats.three_a : stats.fga}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.ftm}</td>
                            <td className="px-3 py-2 text-sm text-center">{stats.fta}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
